import { Map } from "immutable";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { InjectedFormProps } from "redux-form";
import { reduxForm } from "redux-form/immutable";
import ConfirmModal from "../../../../../components/confirm-modal";
import ErrorMessage from "../../../../../components/error-message";
import { RootReduxState } from "../../../../../utils/types";
import { checkStatusAction } from "../../redux/auth/actions";
import { updateIntegrationAction } from "../../redux/shopData/actions";
import { UsernamePasswordForm } from "./UsernamePasswordForm";

type UsernamePasswordModalComponentOwnProps = {
  onClose: () => void;
  onSubmit: (values: Map<unknown, unknown>) => void;
  onSuccess: () => void;
  integrationType: string;
  selectedShop: string;
  id: number;
  authKeyVisible?: boolean;
};

const mapStateToProps = (state: RootReduxState, props: UsernamePasswordModalComponentOwnProps) => ({
  status: state.getIn(["Auth", props.selectedShop, props.integrationType, props.id, "status"]) as
    | string
    | undefined,
  token: state.getIn(["Auth", props.selectedShop, props.integrationType, props.id, "token"]) as
    | string
    | undefined,
  success: state.getIn(["Auth", props.selectedShop, props.integrationType, props.id, "success"]) as
    | boolean
    | undefined,
  loading: state.getIn(["Auth", props.selectedShop, props.integrationType, props.id, "loading"]) as
    | boolean
    | undefined,
  authError: state.getIn(["Auth", props.selectedShop, props.integrationType, props.id, "error"]) as
    | string
    | undefined,
  redirectUrl: state.getIn([
    "Auth",
    props.selectedShop,
    props.integrationType,
    props.id,
    "redirectUrl"
  ]) as string | undefined
});
const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      checkStatusAction,
      updateIntegrationAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type UsernamePasswordModalComponentProps = UsernamePasswordModalComponentOwnProps &
  ConnectedProps<typeof connector>;

function UsernamePasswordModalComponent(
  props: UsernamePasswordModalComponentProps &
    InjectedFormProps<Map<unknown, unknown>, UsernamePasswordModalComponentProps>
) {
  const {
    onClose,
    handleSubmit,
    onSubmit,
    integrationType,
    selectedShop,
    status,
    id,
    success,
    redirectUrl,
    checkStatusAction,
    updateIntegrationAction,
    token,
    authError,
    loading,
    onSuccess,
    authKeyVisible = false
  } = props;

  const [checkInterval, setCheckInterval] = useState<number | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (status === "accepted") {
      updateIntegrationAction(integrationType, selectedShop, id);
      window.clearInterval(checkInterval);
      setCheckInterval(undefined);
      onSuccess();
    } else if (status === "error") {
      if (checkInterval) {
        window.clearInterval(checkInterval);
        setCheckInterval(undefined);
        setErrorMessage(authError || "");
      }
    }
  }, [status]);

  useEffect(() => {
    if (success && redirectUrl) {
      const checkInterval = window.setInterval(() => {
        if (!token) {
          return;
        }

        checkStatusAction(integrationType, selectedShop, token, id);
      }, 1000);
      setCheckInterval(checkInterval);
    }
  }, [success, redirectUrl]);

  const handleClose = useCallback(() => {
    window.clearInterval(checkInterval);
    setCheckInterval(undefined);
    onClose();
  }, [checkInterval, onClose]);

  return (
    <ConfirmModal
      title={
        <FormattedMessage
          id="containers.Integrations.AddNewIntegration.ConnectWindow.Headline"
          defaultMessage="Connect your account"
        />
      }
      leftButtonOnClick={handleClose}
      rightButtonOnClick={handleSubmit(onSubmit)}
      leftButtonText={
        <FormattedMessage id="containers.Dashboard.cancelButton" defaultMessage="Cancel" />
      }
      rightButtonText={
        <FormattedMessage
          id="containers.Integrations.AddNewIntegration.ConnectWindow.ConnectButton"
          defaultMessage="Connect"
        />
      }
      close={handleClose}
      loading={status === "pending" || loading}
      visible
    >
      <span style={{ fontWeight: 500 }}>
        <FormattedMessage
          id={`containers.Integrations.${integrationType}.startIntegrationHeadline`}
          defaultMessage="Please enter you username and password to connect"
        />
      </span>
      <UsernamePasswordForm integrationType={integrationType} authKeyVisible={authKeyVisible} />
      <ErrorMessage>{errorMessage}</ErrorMessage>
    </ConfirmModal>
  );
}

const WrappedForm = reduxForm<Map<unknown, unknown>, UsernamePasswordModalComponentProps>({
  form: "IntegrationForm_AuthForm"
})(UsernamePasswordModalComponent);

export const UsernamePasswordModal = connector(WrappedForm);
